import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import SearchPopover from "@app/component/leadFinder/components/SearchPopover";
import {
  setConfigurationVisibility,
  showLeadFinder,
} from "@app/component/leadFinder/store/actions";
import {
  getLeadFinderLocation,
  getLeadFinderMode,
  getLeadFinderSearch,
  getLeadFinderSearchDistance,
  isConfigurationVisible,
} from "@app/component/leadFinder/store/selectors";
import localSettings from "@app/config/LocalSettings";
import AreaSearchMode from "@app/enum/AreaSearchMode";
import { RootState } from "@app/store/rootReducer";
import MapViewportState from "@app/types/map/MapViewportState";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import styles from "./SearchAreaBar.module.scss";

interface SearchAreaBarProps {
  analyticsIssuer?: AnalyticsService;
  location?: string;
  mode: AreaSearchMode;
  onBeforeSearch?: () => void;
  onSearchSuccess?: (bounds?: google.maps.LatLngBounds) => void;
  query?: string;
  radius: number;
  setConfigurationVisibility: typeof setConfigurationVisibility;
  showLeadFinder: typeof showLeadFinder;
  viewport?: MapViewportState;
  visible: boolean;
}

const messages = defineMessages({
  [AreaSearchMode.ADDRESS]: {
    id: "leadFinder.searchPopover.searchAreaBar.area.address",
    defaultMessage: "a specific address",
    description: "Your current location label",
  },
  [AreaSearchMode.CURRENT_MAP_VIEW_CENTER]: {
    id: "leadFinder.searchPopover.searchAreaBar.area.currentView",
    defaultMessage: "your current map view",
    description: "Your current map view label",
  },
  [AreaSearchMode.CURRENT_USER_LOCATION]: {
    id: "leadFinder.searchPopover.searchAreaBar.area.currentLocation",
    defaultMessage: "your current location",
    description: "Your current location label",
  },
  [AreaSearchMode.ENTITY]: {
    id: "leadFinder.searchPopover.searchAreaBar.area.entity",
    defaultMessage: "a specific Company or Person",
    description: "Your current location label",
  },
  format: {
    id: "leadFinder.searchPopover.searchAreaBar.format",
    defaultMessage: "{distance} around {mode}",
    description: "Kilometer unit in custom distance menu",
  },
  km: {
    id: "leadFinder.searchPopover.searchAreaBar.radius.km",
    defaultMessage: "{distance} km",
    description: "Kilometer unit in custom distance menu",
  },
  miles: {
    id: "leadFinder.searchPopover.searchAreaBar.radius.miles",
    defaultMessage: "{distance} {distance, plural, =0 {miles} one {mile} other {miles}}",
    description: "Mile unit in custom distance menu",
  },
});

const SearchAreaBar = ({
  analyticsIssuer,
  location,
  mode,
  onBeforeSearch,
  onSearchSuccess,
  query,
  radius,
  setConfigurationVisibility,
  showLeadFinder,
  viewport,
  visible,
}: SearchAreaBarProps) => {
  const intl = useIntl();

  const doesUseSiUnits = localSettings.doesUseSiUnits();
  const distanceFormatMessage = doesUseSiUnits ? messages.km : messages.miles;

  const handleVisibleChange = useCallback(
    (visible: boolean) => {
      if (visible) {
        showLeadFinder();
        analyticsIssuer?.initiated(["Find Leads button re-search"]);
      } else {
        setConfigurationVisibility({ visible: false });
      }
    },
    [analyticsIssuer, setConfigurationVisibility, showLeadFinder]
  );

  return (
    <div className={styles.container}>
      <div>
        <Popover
          arrowContent={null}
          content={
            <SearchPopover
              changeQuery
              onBeforeSearch={onBeforeSearch}
              onSearchSuccess={onSearchSuccess}
              viewport={viewport}
            />
          }
          destroyTooltipOnHide
          onVisibleChange={handleVisibleChange}
          overlayClassName={styles.overlay}
          placement="bottomRight"
          trigger="click"
          visible={visible}
        >
          <Button className={styles.btn} leftIcon={<FontAwesomeIcon icon={faPen} />} type="link">
            <div className={styles.info}>
              <span>&ldquo;{query}&ldquo;</span>
              <span>
                {intl.formatMessage(messages.format, {
                  distance: intl.formatMessage(distanceFormatMessage, { distance: radius }),
                  mode: intl.formatMessage(messages[mode]),
                })}
              </span>
            </div>
          </Button>
        </Popover>
      </div>

      {location && (
        <div className={styles.location}>
          <span>{location}</span>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  location: getLeadFinderLocation(state),
  mode: getLeadFinderMode(state),
  query: getLeadFinderSearch(state),
  radius: getLeadFinderSearchDistance(state),
  visible: isConfigurationVisible(state),
});

const mapDispatchToProps = {
  setConfigurationVisibility,
  showLeadFinder,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchAreaBar);
